import axios from 'axios';
import { handleHttpError } from './errorHandler';

// 開發：Vite proxy 轉發「同源」的 /api/*；axios base 留空，避免與 /api 路徑重複拼接。
// 預設 API 走 Docker（gonghao_api nginx 對外埠 8101，見 backend/.env 的 VITE_API_BASE_URL）。
// 上線：VITE_API_BASE_URL 指向 API 根（不含 path），各請求仍用 /api/... 路徑。
const isDev = import.meta.env.DEV;

const normalizeBaseURL = (raw) => {
    const v = String(raw || '').trim();
    if (!v) return v;
    if (v.startsWith('/')) return v; // 允許相對路徑
    if (/^https?:\/\//i.test(v)) return v;
    // 避免像 `127.0.0.1:9000` 被瀏覽器當成相對路徑拼接成 `http://host/127.0.0.1:9000/...`
    return `http://${v}`;
};

const baseURL = isDev ? '' : normalizeBaseURL(import.meta.env.VITE_API_BASE_URL);

/** 一律從網站根路徑請求，避免 SPA 深層路由把 api/admin/... 拼到錯誤路徑 */
const toRootApiPath = (url) => {
    const path = String(url || '').trim();
    if (!path) return path;
    if (/^https?:\/\//i.test(path) || path.startsWith('/')) return path;
    return `/${path}`;
};

/**
 * @type {import('axios').AxiosInstance & { POST: Function, GET: Function, PUT: Function, DELETE: Function, PATCH: Function }}
 */
const apiService = axios.create({ baseURL });

/**
 * 通用請求方法
 * @param {string} method - HTTP 方法（GET、POST、PUT、DELETE 等）
 * @param {string} url - 請求的 URL
 * @param {object} data - 請求的數據
 * @param {object} customHeaders {ContentType: 'multipart/form-data', ResponseType: 'blob'} - 自定義標頭，參數務必要大寫 ContentType, ResponseType
 * @param {object} params - GET 請求的查詢參數
 * @returns {Promise} - 返回的數據
 */
apiService.request = async function (
    method,
    url,
    data = {},
    customHeaders = {},
    params = {}
) {
    try {
        // 取得 localStorage 中的 accessToken
        const accessToken = localStorage.getItem('accessToken');

        // 設定預設 headers；FormData 勿手動指定 Content-Type，需由瀏覽器帶 boundary
        const headers = {};
        if (!(data instanceof FormData)) {
            headers['Content-Type'] = customHeaders.ContentType
                ? customHeaders.ContentType
                : 'application/json';
        } else if (customHeaders.ContentType && customHeaders.ContentType !== 'multipart/form-data') {
            headers['Content-Type'] = customHeaders.ContentType;
        }

        const isLoginRequest = /\/(?:api\/admin\/)?login$/.test(toRootApiPath(url));

        // 登入端點不帶舊 token，避免過期 token 干擾重新登入。
        if (!isLoginRequest && accessToken) {
            headers.Authorization = `Bearer ${accessToken}`;
        }

        // 如果有自定義的 Authorization，則使用自定義的
        if (customHeaders.Authorization) {
            headers.Authorization = customHeaders.Authorization;
        }

        // 設定 axios config
        const config = {
            method,
            url: toRootApiPath(url),
            data,
            headers,
            params
        };

        // 如果有指定 ResponseType，則加入（例如下載檔案用 'blob'）
        if (customHeaders.ResponseType) {
            config.responseType = customHeaders.ResponseType;
        }

        const response = await this(config);

        return response.data;
    } catch (error) {
        return handleHttpError(error, url, method);
    }
};

/**
 * 創建 HTTP 方法函數
 * @param {string} method - HTTP 方法（GET、POST、PUT、DELETE 等）
 * @returns {function} - 對應的 HTTP 方法函數
 */
function createHttpMethod(method) {
    return function (url, dataOrHeaders = {}, customHeaders = {}) {
        const data = ['post', 'put', 'patch', 'delete'].includes(method)
            ? dataOrHeaders
            : {};
        const headers = customHeaders;
        const params = ['get'].includes(method) ? dataOrHeaders : {};

        return apiService.request(method, url, data, headers, params);
    };
}

// 定義各種 HTTP 方法
apiService.GET = createHttpMethod('get');
apiService.POST = createHttpMethod('post');
apiService.PUT = createHttpMethod('put');
apiService.DELETE = createHttpMethod('delete');
apiService.PATCH = createHttpMethod('patch');

// 在請求攔截器中使用
axios.interceptors.response.use(
    (response) => response,
    (error) => handleHttpError(error)
);

export default apiService;
